/*
** Constants
*/

const SVG_NS = 'http://www.w3.org/2000/svg';


/*
** Utility functions
*/

function normalizePointsArray(points) {
  if (points.length) {
    if ('x' in points[0] && 'y' in points[0]) {
      return points.map(point => [point.x, point.y]);
    } else if (!(points[0] instanceof Array)) {
      return points.map((point, index) => [index, point]);
    }
  }

  return points;
}

function pointsToPath(points) {
  let pathData = '';

  if (points instanceof Array && points.length) {
    points.forEach((point, index) => {
      pathData += ` ${point[0]} ${point[1]}`;

      if (index === 0) {
        pathData = `M${pathData} L`;
      }
    });
  }

  return pathData;
}


/*
** Private variables
*/

let rootSvgIndex = 0;
let clipPathIndex = 0;
let maskIndex = 0;


/*
** Abstract classes
*/

class Element {
  constructor(tagName) {
    this.node = document.createElementNS(SVG_NS, tagName);
  }

  attr(prop, value) {
    if (typeof prop === 'object') {
      Object.keys(prop).forEach((key) => {
        this.node.setAttribute(key, prop[key]);
      });
    } else if (typeof value === 'undefined') {
      const val = this.node.getAttribute(prop);
      return isNaN(val) || val === '' ? val : Number(val); // eslint-disable-line no-restricted-globals
    } else {
      this.node.setAttribute(prop, value);
    }
    return this;
  }

  addClass(cls) {
    if (this.node.classList) {
      cls.split(' ').filter(c => c !== '').forEach(c => this.node.classList.add(c));
    } else if (!this.hasClass(cls)) {
      this.attr('class', `${this.node.getAttribute('class') !== null ? this.node.getAttribute('class') : ''} ${cls}`);
    }
    return this;
  }

  removeClass(cls) {
    if (this.node.classList) {
      cls.split(' ').forEach(c => this.node.classList.remove(c));
    } else {
      this.attr('class', this.attr('class').replace(new RegExp(`(^|\\b)${cls.split(' ').join('|')}(\\b|$)`, 'gi'), ' '));
    }
    return this;
  }

  toggleClass(cls, force) {
    if (force === true) {
      this.addClass(cls);
    } else if (force === false) {
      this.removeClass(cls);
    } else if (this.node.classList) {
      this.node.classList.toggle(cls);
    } else if (this.hasClass(cls)) {
      this.removeClass(cls);
    } else {
      this.addClass(cls);
    }

    return this;
  }

  hasClass(cls) {
    if (this.node.classList) {
      return this.node.classList.contains(cls);
    }
    return new RegExp(`(^| )${cls}( |$)`, 'gi').test(this.attr('class'));
  }

  appendTo(el) {
    if (el instanceof Element) {
      el.node.appendChild(this.node);
    } else {
      el.appendChild(this.node);
    }
    return this;
  }

  appendChild(el) {
    this.node.appendChild(el instanceof Element ? el.node : el);
    return this;
  }

  remove() {
    if (this.node.parentNode) {
      this.node.parentNode.removeChild(this.node);
    }
    return this;
  }

  removeChild(el) {
    this.node.removeChild(el instanceof Element ? el.node : el);
    return this;
  }

  show() {
    this.node.style.display = '';
    return this;
  }

  hide() {
    this.node.style.display = 'none';
    return this;
  }

  bbox() {
    const {
      x,
      y,
      width,
      height,
    } = this.node.getBBox();
    return {
      x,
      y,
      width,
      height,
      left: x,
      top: y,
      right: x + width,
      bottom: y + height,
    };
  }

  clip(clipPath) {
    let id;
    if (clipPath instanceof Element) {
      id = clipPath.attr('id');
    } else if (clipPath instanceof SVGElement) {
      id = clipPath.id; // eslint-disable-line prefer-destructuring
    } else {
      id = clipPath;
    }

    return this.attr('clip-path', `url(#${id})`);
  }

  mask(mask) {
    let id;
    if (mask instanceof Element) {
      id = mask.attr('id');
    } else if (mask instanceof SVGElement) {
      id = mask.id; // eslint-disable-line prefer-destructuring
    } else {
      id = mask;
    }

    return this.attr('mask', `url(#${id})`);
  }

  parent() {
    return this.node.parentNode;
  }

  front() {
    this.parent().appendChild(this.node);
    return this;
  }

  back() {
    this.parent().insertBefore(this.node, this.parent().firstChild);
    return this;
  }

  appendRawSVG(svg) {
    // temporary container element
    let container = document.createElementNS(SVG_NS, 'svg');

    // IE and Edge don't support innerHTML on <svg> elements
    if ('innerHTML' in container) {
      container.innerHTML = svg;
    } else {
      const div = document.createElement('div');
      div.innerHTML = `<svg>${svg}</svg>`;
      container = div.children[0]; // eslint-disable-line prefer-destructuring
    }

    // move child nodes
    while (container.firstChild) {
      this.appendChild(container.firstChild);
    }

    return this;
  }
}

class PositionedElement extends Element {
  x(value) {
    return this.attr('x', value);
  }

  y(value) {
    return this.attr('y', value);
  }

  move(x, y) {
    this.x(x);
    this.y(y);
    return this;
  }

  cx(value) {
    const halfWidth = this.bbox().width / 2;
    if (typeof value === 'undefined') {
      return this.x() + halfWidth;
    }
    return this.x(value - halfWidth);
  }

  cy(value) {
    const halfHeight = this.bbox().height / 2;
    if (typeof value === 'undefined') {
      return this.y() + halfHeight;
    }
    return this.y(value - halfHeight);
  }
}

class RectangularElement extends PositionedElement {
  width(value) {
    return this.attr('width', value);
  }

  height(value) {
    return this.attr('height', value);
  }

  size(...args) {
    if (!args.length) {
      return { width: this.width(), height: this.height() };
    }
    this.width(args[0]).height(args[1]);
    return this;
  }
}

class CircularElement extends Element {
  cx(value) {
    return this.attr('cx', value);
  }

  cy(value) {
    return this.attr('cy', value);
  }
}

class PolygonalElement extends Element {
  constructor(tagName, points) {
    super(tagName);
    this.points = [];

    if (typeof points !== 'undefined') {
      this.setPoints(points);
    }
  }

  setPoints(points) {
    this.points = normalizePointsArray(points);
    this.attr('points', this.points.map(point => `${point.x},${point.y}`).join(' '));
    return this;
  }
}

class TextElement extends PositionedElement {
  dx(value) {
    return this.attr('dx', value);
  }

  dy(value) {
    return this.attr('dy', value);
  }
}


/*
** Wrapper classes for SVG elements
*/

export class SVG extends RectangularElement {
  constructor() {
    super('svg');
    this.attr('xmlns', SVG_NS);
    this.attr('id', `dv-svg-${rootSvgIndex++}`);
    this.createDefs();
  }

  createDefs() {
    this.defs = new Element('defs');
    this.appendChild(this.defs);
    return this;
  }

  addDef(el) {
    this.defs.appendChild(el);
    return this;
  }

  removeDef(el) {
    this.defs.removeChild(el);
    return this;
  }

  removeAllChildren() {
    while (this.node.firstChild) {
      this.removeChild(this.node.firstChild);
    }
    this.createDefs();
    return this;
  }
}

export class EmbeddedSVG extends RectangularElement {
  constructor() {
    super('svg');
  }
}

export class Rect extends RectangularElement {
  constructor(width, height) {
    super('rect');
    this.width(width).height(height);
  }

  rx(value) {
    return this.attr('rx', value);
  }

  ry(value) {
    return this.attr('ry', value);
  }
}

export class Circle extends CircularElement {
  constructor(r) {
    super('circle');
    this.r(r);
  }

  r(value) {
    return this.attr('r', value);
  }
}

export class Ellipse extends CircularElement {
  constructor() {
    super('ellipse');
  }

  rx(value) {
    return this.attr('rx', value);
  }

  ry(value) {
    return this.attr('ry', value);
  }
}

export class Tspan extends TextElement {
  constructor(text) {
    super('tspan');
    this.text(text);
  }

  text(text) {
    if (typeof text === 'undefined') {
      return this.node.textContent;
    }
    this.node.textContent = text;
    return this;
  }
}

export class Text extends TextElement {
  constructor(text, leading = 1) {
    super('text');

    this.leading(leading);
    this.lines = [];
    this.text(text);
  }

  clear() {
    while (this.node.firstChild) {
      this.removeChild(this.node.firstChild);
    }
    this.lines = [];
    return this;
  }

  text(text) {
    if (typeof text === 'undefined') {
      return this.textContent;
    }

    this.clear();
    text.split('\n').forEach((line, index) => {
      this.lines.push(new Tspan(line.length ? line : ' ')
        .x(this.x())
        .dy(this.getLineOffset(index === 0))
        .appendTo(this));
    });

    return this;
  }

  x(value) {
    if (typeof value === 'undefined') {
      return this.attr('x');
    }

    this.attr('x', value);
    this.lines.forEach(line => line.x(value));
    return this;
  }

  getLines() {
    return this.lines;
  }

  leading(value) {
    if (typeof value === 'undefined') {
      return this.textLeading;
    }
    this.textLeading = value;
    return this;
  }

  getLineOffset(firstLine = false) {
    return firstLine ? `${0.8 + ((this.textLeading - 1) / 2)}em` : `${this.textLeading}em`;
  }
}

export class Group extends Element {
  constructor() {
    super('g');
    this.translateX = 0;
    this.translateY = 0;
  }

  x(value) {
    if (typeof value === 'undefined') {
      return this.translateX;
    }
    this.translateX = value;
    return this.attr('transform', `translate(${this.translateX}, ${this.translateY})`);
  }

  y(value) {
    if (typeof value === 'undefined') {
      return this.translateY;
    }
    this.translateY = value;
    return this.attr('transform', `translate(${this.translateX}, ${this.translateY})`);
  }

  bbox() {
    const x = this.x();
    const y = this.y();
    const { width, height } = this.node.getBBox();
    return {
      x,
      y,
      width,
      height,
      left: x,
      top: y,
      right: x + width,
      bottom: y + height,
    };
  }

  children() {
    return this.node.children || [...this.node.childNodes].filter(node => node instanceof SVGElement);
  }

  child(index) {
    return this.children()[index];
  }
}

export class Line extends Element {
  constructor(x1, y1, x2, y2) {
    super('line');

    if (x1 !== 0) {
      this.attr('x1', x1);
    }
    if (y1 !== 0) {
      this.attr('y1', y1);
    }
    if (x2 !== 0) {
      this.attr('x2', x2);
    }
    if (y2 !== 0) {
      this.attr('y2', y2);
    }
  }
}

export class Polyline extends PolygonalElement {
  constructor(points) {
    super('polyline', points);
  }
}

export class Polygon extends PolygonalElement {
  constructor(points) {
    super('polygon', points);
  }
}

export class Path extends PolygonalElement {
  constructor(points) {
    super('path', points);
  }

  setPoints(points) {
    this.points = normalizePointsArray(points);
    this.attr('d', pointsToPath(this.points));
    return this;
  }
}

export class ClipPath extends PositionedElement {
  constructor() {
    super('clipPath');
    this.attr('id', `svg-cp${clipPathIndex++}`);
  }
}

export class Mask extends RectangularElement {
  constructor() {
    super('mask');
    this.attr('id', `svg-mask${maskIndex++}`);
  }
}
